// src/pages/Users/Inscription/AttestationPage.jsx

import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import jsPDF from "jspdf";

import useScrollToTop from "./../../../hooks/useScollToTop";
import { toAbsolutePhotoUrl } from "../../../services/settingsPhotoServices";

import "./AttestationPage.css";

/* -------------------------------------------------------------------------- */
/*                                   Couleurs                                 */
/* -------------------------------------------------------------------------- */

const COLORS = {
  navy: [1, 62, 142],
  green: [76, 168, 47],

  slate900: [15, 23, 42],
  slate700: [51, 65, 85],
  slate500: [100, 116, 139],
  slate300: [203, 213, 225],
  slate200: [226, 232, 240],
  slate100: [241, 245, 249],

  white: [255, 255, 255],

  emerald: [5, 150, 105],
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const toNumberSafe = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

const formatEUR = (value) => {
  return `${toNumberSafe(value).toFixed(2).replace(".", ",")} €`;
};

const cleanFileName = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

/* -------------------------------------------------------------------------- */
/*                         Chargement du logo pour jsPDF                       */
/* -------------------------------------------------------------------------- */

/**
 * Convertit une URL d'image en PNG exploitable par jsPDF.
 *
 * On passe volontairement par fetch + Blob + Canvas pour que :
 * - JPG fonctionne
 * - PNG fonctionne
 * - WEBP fonctionne
 *
 * Puis jsPDF reçoit toujours un PNG.
 *
 * Si l'image ne peut pas être chargée, le PDF est quand même généré.
 */
const imageUrlToPngDataUrl = async (url) => {
  if (!url) return null;

  try {
    const response = await fetch(url, {
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          try {
            /*
             * On évite de fabriquer un canvas gigantesque
             * si le logo original fait plusieurs milliers de pixels.
             */
            const maxSize = 1200;

            const naturalWidth = img.naturalWidth || img.width;
            const naturalHeight = img.naturalHeight || img.height;

            const ratio = Math.min(
              1,
              maxSize / naturalWidth,
              maxSize / naturalHeight,
            );

            const width = Math.max(1, Math.round(naturalWidth * ratio));
            const height = Math.max(1, Math.round(naturalHeight * ratio));

            const canvas = document.createElement("canvas");

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");

            if (!ctx) {
              reject(new Error("Impossible de créer le canvas."));
              return;
            }

            /*
             * Fond blanc volontaire.
             * Très pratique pour les logos transparents.
             */
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, width, height);

            ctx.drawImage(img, 0, 0, width, height);

            resolve(canvas.toDataURL("image/png"));
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => {
          reject(new Error("Impossible de charger le logo."));
        };

        img.src = objectUrl;
      });

      return dataUrl;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    console.warn(
      "[Attestation PDF] Le logo n'a pas pu être chargé :",
      error,
    );

    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                              Bloc informations                              */
/* -------------------------------------------------------------------------- */

const drawRoundedBlock = ({
  doc,
  x,
  y,
  w,
  h,
  title,
  lines = [],
}) => {
  doc.setDrawColor(...COLORS.slate200);
  doc.setFillColor(...COLORS.white);

  doc.roundedRect(x, y, w, h, 4, 4, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.navy);

  doc.text(title, x + 5, y + 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.slate700);

  let cursorY = y + 15;

  lines.filter(Boolean).forEach((line) => {
    const wrapped = doc.splitTextToSize(String(line), w - 10);

    doc.text(wrapped, x + 5, cursorY);

    cursorY += wrapped.length * 4.3;
  });
};

/* -------------------------------------------------------------------------- */
/*                              Ligne règlement                                */
/* -------------------------------------------------------------------------- */

const drawPaymentLine = ({
  doc,
  label,
  value,
  x,
  y,
  width,
  bold = false,
}) => {
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.slate700);

  doc.text(label, x, y);

  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setTextColor(...COLORS.slate900);

  doc.text(String(value), x + width, y, {
    align: "right",
  });
};

/* -------------------------------------------------------------------------- */
/*                                   Footer                                   */
/* -------------------------------------------------------------------------- */

const drawFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setDrawColor(...COLORS.slate200);

  doc.line(
    16,
    pageHeight - 18,
    pageWidth - 16,
    pageHeight - 18,
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.slate500);

  doc.text(
    "Document généré automatiquement depuis l'espace adhérent MonAppliClub.",
    16,
    pageHeight - 11,
  );
};

const drawFootersOnAllPages = (doc) => {
  const pages = doc.getNumberOfPages();

  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    drawFooter(doc);
  }
};

/* -------------------------------------------------------------------------- */
/*                                Logo PDF                                    */
/* -------------------------------------------------------------------------- */

const drawLogo = async ({
  doc,
  logoUrl,
  x,
  y,
  boxWidth,
  boxHeight,
}) => {
  if (!logoUrl) return;

  const dataUrl = await imageUrlToPngDataUrl(logoUrl);

  if (!dataUrl) return;

  try {
    const imageProperties = doc.getImageProperties(dataUrl);

    const imageWidth = imageProperties.width;
    const imageHeight = imageProperties.height;

    const ratio = Math.min(
      boxWidth / imageWidth,
      boxHeight / imageHeight,
    );

    const width = imageWidth * ratio;
    const height = imageHeight * ratio;

    const imageX = x + (boxWidth - width) / 2;
    const imageY = y + (boxHeight - height) / 2;

    doc.addImage(
      dataUrl,
      "PNG",
      imageX,
      imageY,
      width,
      height,
    );
  } catch (error) {
    console.warn(
      "[Attestation PDF] Impossible d'ajouter le logo :",
      error,
    );
  }
};

/* -------------------------------------------------------------------------- */
/*                          Génération de l'attestation                         */
/* -------------------------------------------------------------------------- */

const generateAttestationPdf = async ({
  prenom,
  nom,
  dateNaissance,

  adresse,
  cp,
  commune,

  cotisation,
  licence,

  reductionFoyer,
  passSport,
  reductionSaison,

  typeReglement,
  nbReglement,
  statutReglement,
  observations,

  datePaiement,

  president,
  association,

  logo,

  adresse_club,
  tel_club,
  mail_club,
  siteweb_club,
  siret_club,

  saison_active,
}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();

  const margin = 16;

  /* ------------------------------------------------------------------------ */
  /*                                  Valeurs                                 */
  /* ------------------------------------------------------------------------ */

  const montantCotisation = toNumberSafe(cotisation);
  const montantLicence = toNumberSafe(licence);

  const montantReductionFoyer = toNumberSafe(reductionFoyer);
  const montantPassSport = toNumberSafe(passSport);
  const montantReductionSaison = toNumberSafe(reductionSaison);

  /*
   * On conserve volontairement ta logique actuelle :
   *
   * cotisation
   * + licence
   * + réduction foyer
   * + Pass'Sport
   * + réduction saison
   *
   * Donc tes réductions peuvent rester enregistrées en négatif
   * comme actuellement.
   */
  const total =
    montantCotisation +
    montantLicence +
    montantReductionFoyer +
    montantPassSport +
    montantReductionSaison;

  const membre =
    [prenom, nom?.toUpperCase()]
      .filter(Boolean)
      .join(" ") || "Adhérent";

  const adresseAdherent = [
    adresse,
    [cp, commune].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" - ");

  const logoUrl = toAbsolutePhotoUrl(
    String(logo || "").trim(),
  );

  /* ------------------------------------------------------------------------ */
  /*                                  Header                                  */
  /* ------------------------------------------------------------------------ */

  doc.setFillColor(...COLORS.navy);
  doc.rect(0, 0, pageWidth, 38, "F");

  doc.setFillColor(...COLORS.green);
  doc.rect(0, 38, pageWidth, 2, "F");

  /*
   * Nom du club
   */
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.white);

  const clubName = association || "MonAppliClub";

  const wrappedClubName = doc.splitTextToSize(
    clubName,
    120,
  );

  doc.text(
    wrappedClubName.slice(0, 2),
    margin,
    10,
  );

  /*
   * Titre
   */
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.white);

  doc.text(
    "ATTESTATION D'INSCRIPTION",
    margin,
    23,
  );

  /*
   * Saison
   */
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  doc.text(
    `Saison ${saison_active || "—"}`,
    margin,
    31,
  );

  /* ------------------------------------------------------------------------ */
  /*                                   Logo                                   */
  /* ------------------------------------------------------------------------ */

  /*
   * Fond blanc derrière le logo
   */
  const logoBoxX = pageWidth - margin - 28;

  doc.setFillColor(...COLORS.white);

  doc.roundedRect(
    logoBoxX,
    5,
    28,
    28,
    4,
    4,
    "F",
  );

  await drawLogo({
    doc,
    logoUrl,
    x: logoBoxX + 2,
    y: 7,
    boxWidth: 24,
    boxHeight: 24,
  });

  /* ------------------------------------------------------------------------ */
  /*                              Badge + date                                */
  /* ------------------------------------------------------------------------ */

  doc.setFillColor(...COLORS.emerald);

  doc.roundedRect(
    margin,
    47,
    42,
    10,
    5,
    5,
    "F",
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.white);

  doc.text(
    "ATTESTATION",
    margin + 21,
    53.5,
    {
      align: "center",
    },
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.slate500);

  doc.text(
    `Date d'émission : ${datePaiement || "—"}`,
    pageWidth - margin,
    53,
    {
      align: "right",
    },
  );

  /* ------------------------------------------------------------------------ */
  /*                       Structure / informations club                       */
  /* ------------------------------------------------------------------------ */

  const blocksY = 65;
  const blockWidth = 86;
  const blockHeight = 45;

  drawRoundedBlock({
    doc,
    x: margin,
    y: blocksY,
    w: blockWidth,
    h: blockHeight,
    title: "Structure",
    lines: [
      association,

      adresse_club,

      tel_club
        ? `Téléphone : ${tel_club}`
        : "",

      mail_club
        ? `Email : ${mail_club}`
        : "",

      siteweb_club
        ? `Site : ${siteweb_club}`
        : "",

      siret_club
        ? `SIRET : ${siret_club}`
        : "",
    ],
  });

  /* ------------------------------------------------------------------------ */
  /*                            Informations adhérent                          */
  /* ------------------------------------------------------------------------ */

  drawRoundedBlock({
    doc,
    x: margin + 92,
    y: blocksY,
    w: blockWidth,
    h: blockHeight,
    title: "Adhérent",
    lines: [
      membre,

      dateNaissance
        ? `Né(e) le : ${dateNaissance}`
        : "",

      adresseAdherent,

      saison_active
        ? `Saison : ${saison_active}`
        : "",
    ],
  });

  /* ------------------------------------------------------------------------ */
  /*                             Texte attestation                             */
  /* ------------------------------------------------------------------------ */

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.slate700);

  const certificationText =
    `Je soussigné ${president || "le/la responsable du club"}, ` +
    `Président(e) de l'association ${association || ""}, certifie que ` +
    `${membre} est inscrit(e) au sein de notre association pour la saison ` +
    `${saison_active || "en cours"}.`;

  const certificationLines =
    doc.splitTextToSize(
      certificationText,
      pageWidth - margin * 2,
    );

  doc.text(
    certificationLines,
    margin,
    122,
  );

  /* ------------------------------------------------------------------------ */
  /*                           Bloc règlement                                  */
  /* ------------------------------------------------------------------------ */

  const paymentY = 146;
  const paymentX = margin;
  const paymentWidth =
    pageWidth - margin * 2;

  /*
   * Observations
   */
  let observationLines = [];

  // if (observations) {
  //   doc.setFontSize(7.5);

  //   observationLines =
  //     doc.splitTextToSize(
  //       `Observations : ${observations}`,
  //       paymentWidth - 10,
  //     );
  // }

  const paymentBlockHeight =
    60 +
    Math.max(
      0,
      observationLines.length - 2,
    ) *
      4;

  doc.setDrawColor(...COLORS.slate200);
  doc.setFillColor(...COLORS.white);

  doc.roundedRect(
    paymentX,
    paymentY,
    paymentWidth,
    paymentBlockHeight,
    4,
    4,
    "FD",
  );

  /* ------------------------------------------------------------------------ */
  /*                              Titre bloc                                   */
  /* ------------------------------------------------------------------------ */

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.navy);

  doc.text(
    "Détails du règlement",
    paymentX + 5,
    paymentY + 9,
  );

  /* ------------------------------------------------------------------------ */
  /*                         Séparation colonnes                               */
  /* ------------------------------------------------------------------------ */

  const separatorX =
    paymentX + paymentWidth / 2;

  doc.setDrawColor(...COLORS.slate200);

  doc.line(
    separatorX,
    paymentY + 15,
    separatorX,
    paymentY + 43,
  );

  /* ------------------------------------------------------------------------ */
  /*                         Colonne montants                                  */
  /* ------------------------------------------------------------------------ */

  const leftX = paymentX + 5;
  const leftWidth =
    paymentWidth / 2 - 12;

  let leftY = paymentY + 20;

  drawPaymentLine({
    doc,
    label: "Licence / assurance",
    value: formatEUR(montantLicence),
    x: leftX,
    y: leftY,
    width: leftWidth,
  });

  leftY += 6;

  drawPaymentLine({
    doc,
    label: "Cotisation club",
    value: formatEUR(montantCotisation),
    x: leftX,
    y: leftY,
    width: leftWidth,
  });

  leftY += 6;

  if (montantReductionFoyer !== 0) {
    drawPaymentLine({
      doc,
      label: "Réduction foyer",
      value: formatEUR(
        montantReductionFoyer,
      ),
      x: leftX,
      y: leftY,
      width: leftWidth,
    });

    leftY += 6;
  }

  if (montantPassSport !== 0) {
    drawPaymentLine({
      doc,
      label: "Pass'Sport",
      value: formatEUR(
        montantPassSport,
      ),
      x: leftX,
      y: leftY,
      width: leftWidth,
    });

    leftY += 6;
  }

  if (montantReductionSaison !== 0) {
    drawPaymentLine({
      doc,
      label: "Réduction saison",
      value: formatEUR(
        montantReductionSaison,
      ),
      x: leftX,
      y: leftY,
      width: leftWidth,
    });
  }

  /* ------------------------------------------------------------------------ */
  /*                     Colonne informations paiement                         */
  /* ------------------------------------------------------------------------ */

  const rightX =
    separatorX + 7;

  let rightY = paymentY + 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.slate700);

  if (typeReglement) {
    doc.text(
      `Mode : ${typeReglement}`,
      rightX,
      rightY,
    );

    rightY += 7;
  }

  if (
    nbReglement !== undefined &&
    nbReglement !== null &&
    Number(nbReglement) !== 0
  ) {
    doc.text(
      `Nombre de règlements : ${nbReglement}`,
      rightX,
      rightY,
    );

    rightY += 7;
  }

  if (statutReglement) {
    doc.text(
      `Statut : ${statutReglement}`,
      rightX,
      rightY,
    );

    rightY += 7;
  }

  if (datePaiement) {
    doc.text(
      `Date : ${datePaiement}`,
      rightX,
      rightY,
    );
  }

  /* ------------------------------------------------------------------------ */
  /*                              Observations                                 */
  /* ------------------------------------------------------------------------ */

  if (observationLines.length > 0) {
    doc.setDrawColor(...COLORS.slate200);

    doc.line(
      paymentX + 5,
      paymentY + 47,
      paymentX + paymentWidth - 5,
      paymentY + 47,
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.slate500);

    doc.text(
      observationLines,
      paymentX + 5,
      paymentY + 53,
    );
  }

  /* ------------------------------------------------------------------------ */
  /*                              Total                                        */
  /* ------------------------------------------------------------------------ */

  const totalY =
    paymentY +
    paymentBlockHeight +
    6;

  doc.setFillColor(...COLORS.emerald);

  doc.roundedRect(
    paymentX,
    totalY,
    paymentWidth,
    14,
    4,
    4,
    "F",
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.white);

  doc.text(
    "Montant total",
    paymentX + 6,
    totalY + 9,
  );

  doc.setFontSize(13);

  doc.text(
    formatEUR(total),
    paymentX +
      paymentWidth -
      6,
    totalY + 9,
    {
      align: "right",
    },
  );

  /* ------------------------------------------------------------------------ */
  /*                           Signature président                             */
  /* ------------------------------------------------------------------------ */

  let signatureY =
    totalY + 26;

  /*
   * Cas exceptionnel :
   * observations très longues.
   */
  if (signatureY > 258) {
    doc.addPage();

    signatureY = 35;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.navy);

    doc.text(
      "ATTESTATION D'INSCRIPTION",
      margin,
      20,
    );
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.slate700);

  doc.text(
    `Fait le ${datePaiement || "—"}`,
    pageWidth - margin,
    signatureY,
    {
      align: "right",
    },
  );

  doc.text(
    "Le/la Président(e),",
    pageWidth - margin,
    signatureY + 8,
    {
      align: "right",
    },
  );

  doc.setFont(
    "helvetica",
    "bold",
  );

  doc.text(
    president || "—",
    pageWidth - margin,
    signatureY + 15,
    {
      align: "right",
    },
  );

  /* ------------------------------------------------------------------------ */
  /*                                  Footer                                  */
  /* ------------------------------------------------------------------------ */

  drawFootersOnAllPages(doc);

  /* ------------------------------------------------------------------------ */
  /*                             Nom du fichier                                */
  /* ------------------------------------------------------------------------ */

  const fileName = [
    "attestation-inscription",
    cleanFileName(nom || "adherent"),
    cleanFileName(prenom || ""),
    cleanFileName(saison_active || ""),
  ]
    .filter(Boolean)
    .join("-");

  doc.save(`${fileName}.pdf`);
};

/* -------------------------------------------------------------------------- */
/*                              Composant page                                */
/* -------------------------------------------------------------------------- */

const AttestationPage = () => {
  useScrollToTop();

  const location = useLocation();

  const [isGenerating, setIsGenerating] =
    useState(false);

  const {
    prenom,
    nom,
    dateNaissance,

    adresse,
    cp,
    commune,

    cotisation,
    licence,

    reductionFoyer,
    passSport,
    reductionSaison,

    typeReglement,
    nbReglement,
    statutReglement,
    observations,

    datePaiement,

    president,
    association,

    logo,

    adresse_club,
    tel_club,
    mail_club,
    siteweb_club,
    siret_club,

    saison_active,
  } = location.state || {};

  /* ------------------------------------------------------------------------ */
  /*                              Sécurité route                               */
  /* ------------------------------------------------------------------------ */

  if (!location.state) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-32">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center text-amber-900">
          Pas de données disponibles pour cette attestation.
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /*                             Calcul du total                               */
  /* ------------------------------------------------------------------------ */

  const montantCotisation =
    toNumberSafe(cotisation);

  const montantLicence =
    toNumberSafe(licence);

  const montantReductionFoyer =
    toNumberSafe(reductionFoyer);

  const montantPassSport =
    toNumberSafe(passSport);

  const montantReductionSaison =
    toNumberSafe(reductionSaison);

  const totalMontant =
    montantCotisation +
    montantLicence +
    montantReductionFoyer +
    montantPassSport +
    montantReductionSaison;

  /* ------------------------------------------------------------------------ */
  /*                                  Logo                                    */
  /* ------------------------------------------------------------------------ */

  const FALLBACK_LOGO =
    "/img/no-image.jpg";

  const logoSrc =
    toAbsolutePhotoUrl(
      String(logo || "").trim(),
    ) || FALLBACK_LOGO;

  /* ------------------------------------------------------------------------ */
  /*                          Téléchargement PDF                               */
  /* ------------------------------------------------------------------------ */

  const handleDownloadPdf =
    async () => {
      try {
        setIsGenerating(true);

        await generateAttestationPdf({
          prenom,
          nom,
          dateNaissance,

          adresse,
          cp,
          commune,

          cotisation,
          licence,

          reductionFoyer,
          passSport,
          reductionSaison,

          typeReglement,
          nbReglement,
          statutReglement,
          observations,

          datePaiement,

          president,
          association,

          logo,

          adresse_club,
          tel_club,
          mail_club,
          siteweb_club,
          siret_club,

          saison_active,
        });
      } catch (error) {
        console.error(
          "Erreur génération attestation PDF :",
          error,
        );

        window.alert(
          "Une erreur est survenue lors de la génération de l'attestation PDF.",
        );
      } finally {
        setIsGenerating(false);
      }
    };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                  */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-24 font-satoshi dark:bg-slate-950 sm:py-32">
      <div className="mx-auto max-w-4xl">
        {/* --------------------------------------------------------------- */}
        {/*                        Aperçu attestation                        */}
        {/* --------------------------------------------------------------- */}

        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl shadow-slate-900/10">
          {/* Header */}
          <div className="relative bg-[#013e8e] px-6 py-7 text-white sm:px-9">
            <div className="absolute inset-x-0 bottom-0 h-1 bg-[#4ca82f]" />

            <div className="flex items-center justify-between gap-6">
              <div className="min-w-0">
                <div className="text-sm font-semibold uppercase tracking-wide text-blue-100">
                  {association}
                </div>

                <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
                  Attestation d'inscription
                </h1>

                <div className="mt-2 text-sm text-blue-100">
                  Saison{" "}
                  {saison_active ||
                    "—"}
                </div>
              </div>

              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-white p-2 shadow-lg">
                <img
                  src={logoSrc}
                  alt={`Logo ${association || "du club"}`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-9">
            {/* Badge */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="inline-flex w-fit items-center rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white">
                Attestation
              </span>

              <div className="text-sm text-slate-500">
                Date d'émission :{" "}
                <span className="font-semibold text-slate-700">
                  {datePaiement ||
                    "—"}
                </span>
              </div>
            </div>

            {/* Club / adhérent */}
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 p-5">
                <h2 className="text-sm font-bold text-[#013e8e]">
                  Structure
                </h2>

                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p className="font-semibold text-slate-900">
                    {association}
                  </p>

                  {adresse_club && (
                    <p>
                      {adresse_club}
                    </p>
                  )}

                  {tel_club && (
                    <p>
                      Téléphone :{" "}
                      {tel_club}
                    </p>
                  )}

                  {mail_club && (
                    <p>
                      Email :{" "}
                      {mail_club}
                    </p>
                  )}

                  {siteweb_club && (
                    <p>
                      Site :{" "}
                      {siteweb_club}
                    </p>
                  )}

                  {siret_club && (
                    <p>
                      SIRET :{" "}
                      {siret_club}
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-5">
                <h2 className="text-sm font-bold text-[#013e8e]">
                  Adhérent
                </h2>

                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p className="font-semibold text-slate-900">
                    {prenom}{" "}
                    {(nom || "").toUpperCase()}
                  </p>

                  <p>
                    Né(e) le :{" "}
                    {dateNaissance ||
                      "—"}
                  </p>

                  <p>
                    {adresse},{" "}
                    {cp}{" "}
                    {commune}
                  </p>

                  <p>
                    Saison :{" "}
                    {saison_active ||
                      "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Texte */}
            <div className="mt-7 text-sm leading-7 text-slate-700">
              Je soussigné{" "}
              <strong>
                {president}
              </strong>
              , Président(e) de
              l'association{" "}
              <strong>
                {association}
              </strong>
              , certifie que{" "}
              <strong>
                {prenom}{" "}
                {(nom || "").toUpperCase()}
              </strong>{" "}
              est inscrit(e) au sein
              de notre association
              pour la saison{" "}
              <strong>
                {saison_active}
              </strong>
              .
            </div>

            {/* Règlement */}
            <div className="mt-7 rounded-2xl border border-slate-200 p-5">
              <h2 className="text-base font-bold text-[#013e8e]">
                Détails du règlement
              </h2>

              <div className="mt-5 grid gap-6 md:grid-cols-2 md:divide-x md:divide-slate-200">
                {/* Montants */}
                <div className="space-y-3 md:pr-6">
                  <div className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-slate-600">
                      Licence /
                      assurance
                    </span>

                    <strong>
                      {formatEUR(
                        montantLicence,
                      )}
                    </strong>
                  </div>

                  <div className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-slate-600">
                      Cotisation club
                    </span>

                    <strong>
                      {formatEUR(
                        montantCotisation,
                      )}
                    </strong>
                  </div>

                  {montantReductionFoyer !==
                    0 && (
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-slate-600">
                        Réduction
                        foyer
                      </span>

                      <strong>
                        {formatEUR(
                          montantReductionFoyer,
                        )}
                      </strong>
                    </div>
                  )}

                  {montantPassSport !==
                    0 && (
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-slate-600">
                        Pass'Sport
                      </span>

                      <strong>
                        {formatEUR(
                          montantPassSport,
                        )}
                      </strong>
                    </div>
                  )}

                  {montantReductionSaison !==
                    0 && (
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-slate-600">
                        Réduction
                        saison
                      </span>

                      <strong>
                        {formatEUR(
                          montantReductionSaison,
                        )}
                      </strong>
                    </div>
                  )}
                </div>

                {/* Paiement */}
                <div className="space-y-3 md:pl-6">
                  {typeReglement && (
                    <div className="text-sm">
                      <span className="text-slate-500">
                        Mode de
                        règlement
                      </span>

                      <div className="font-semibold text-slate-900">
                        {typeReglement}
                      </div>
                    </div>
                  )}

                  {nbReglement !==
                    undefined &&
                    nbReglement !==
                      null &&
                    Number(
                      nbReglement,
                    ) !== 0 && (
                      <div className="text-sm">
                        <span className="text-slate-500">
                          Nombre de
                          règlements
                        </span>

                        <div className="font-semibold text-slate-900">
                          {nbReglement}
                        </div>
                      </div>
                    )}

                  {statutReglement && (
                    <div className="text-sm">
                      <span className="text-slate-500">
                        Statut
                      </span>

                      <div className="font-semibold text-slate-900">
                        {
                          statutReglement
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* {observations && (
                <div className="mt-5 border-t border-slate-200 pt-4 text-sm text-slate-600">
                  <strong className="text-slate-800">
                    Observations :
                  </strong>{" "}
                  {observations}
                </div>
              )} */}
            </div>

            {/* Total */}
            <div className="mt-5 flex items-center justify-between rounded-2xl bg-emerald-600 px-5 py-4 text-white">
              <span className="font-semibold">
                Montant total
              </span>

              <span className="text-xl font-bold">
                {formatEUR(
                  totalMontant,
                )}
              </span>
            </div>

            {/* Signature */}
            <div className="mt-10 text-right text-sm text-slate-700">
              <p>
                Fait le{" "}
                <strong>
                  {datePaiement ||
                    "—"}
                </strong>
              </p>

              <p className="mt-4">
                Le/la Président(e),
              </p>

              <p className="mt-1 font-bold text-slate-900">
                {president ||
                  "—"}
              </p>
            </div>

            {/* Footer aperçu */}
            <div className="mt-10 border-t border-slate-200 pt-4 text-xs text-slate-400">
              Document généré
              automatiquement depuis
              l'espace adhérent
              MonAppliClub.
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------------- */}
        {/*                            Action                                */}
        {/* --------------------------------------------------------------- */}

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={
              handleDownloadPdf
            }
            disabled={
              isGenerating
            }
            className="inline-flex min-w-[260px] items-center justify-center rounded-xl bg-[#013e8e] px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-800 disabled:cursor-wait disabled:opacity-60"
          >
            {isGenerating
              ? "Génération du PDF..."
              : "Télécharger l'attestation PDF"}
          </button>
        </div>

        <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
          Le document PDF peut
          ensuite être enregistré ou
          imprimé depuis votre lecteur
          PDF.
        </p>
      </div>
    </div>
  );
};

export default AttestationPage;